import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/contexts/LanguageContext";

/**
 * Animated SVG arc — Morocco → Canada over the Atlantic.
 * Arc draws itself as user scrolls into view. Saffron→Teal gradient stroke.
 * Province glow on hover (Quebec, Ontario, BC, Alberta visible).
 */
const MapSection = () => {
  const { t } = useLang();
  const sectionRef = useRef(null);
  const pathRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [pathLength, setPathLength] = useState(2000);

  useEffect(() => {
    if (pathRef.current) {
      setPathLength(pathRef.current.getTotalLength());
    }
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const el = sectionRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const start = vh * 0.9;
      const end = -rect.height * 0.4;
      const raw = (start - rect.top) / (start - end);
      setScrollProgress(Math.max(0, Math.min(1, raw)));
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const dashOffset = pathLength * (1 - scrollProgress);

  return (
    <section
      id="map"
      data-testid="map-section"
      ref={sectionRef}
      className="relative bg-[#0D1117] py-28 sm:py-36 overflow-hidden"
    >
      <div className="noise-soft" />

      <div className="relative max-w-[1320px] mx-auto px-6 lg:px-10">
        <div className="mb-16 max-w-3xl">
          <span className="brushstroke mb-6" />
          <div className="font-mono-accent text-[10px] text-[#E8A045] mb-5">
            {t.map.eyebrow}
          </div>
          <h2
            data-testid="map-headline"
            className="font-display text-5xl sm:text-6xl lg:text-[4.5rem] text-[#F5F2EE] leading-[1.02] tracking-tight mb-6"
          >
            {t.map.headline}
          </h2>
          <p
            data-testid="map-copy"
            className="font-body text-base sm:text-lg text-[#F5F2EE]/60 leading-relaxed max-w-xl"
          >
            {t.map.copy}
          </p>
        </div>

        {/* World map — minimalist, borderless */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1.2 }}
          className="relative w-full"
        >
          <svg
            viewBox="0 0 1200 540"
            xmlns="http://www.w3.org/2000/svg"
            className="w-full h-auto"
          >
            <defs>
              <linearGradient id="map-arc-grad" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#E8A045" />
                <stop offset="100%" stopColor="#2FBFAD" />
              </linearGradient>
              <radialGradient id="origin-glow">
                <stop offset="0%" stopColor="#E8A045" stopOpacity="0.6" />
                <stop offset="100%" stopColor="#E8A045" stopOpacity="0" />
              </radialGradient>
              <radialGradient id="dest-glow">
                <stop offset="0%" stopColor="#2FBFAD" stopOpacity="0.6" />
                <stop offset="100%" stopColor="#2FBFAD" stopOpacity="0" />
              </radialGradient>
              <pattern id="map-dots" width="14" height="14" patternUnits="userSpaceOnUse">
                <circle cx="1" cy="1" r="0.55" fill="#F5F2EE" opacity="0.18" />
              </pattern>
            </defs>

            {/* dot grid background */}
            <rect width="1200" height="540" fill="url(#map-dots)" opacity="0.6" />

            {/* CANADA — simplified outline */}
            <g opacity="0.4">
              <path
                d="M 90 180
                   L 180 130 L 260 110 L 340 120 L 420 100 L 480 130
                   L 510 170 L 470 220 L 510 260 L 480 290 L 410 280
                   L 350 300 L 280 280 L 200 290 L 130 280 L 95 240 Z"
                fill="#F5F2EE"
                opacity="0.06"
                stroke="#F5F2EE"
                strokeWidth="0.6"
              />
              {/* province dividers (Quebec, Ontario, BC, Alberta hinted) */}
              <line x1="200" y1="200" x2="220" y2="270" stroke="#F5F2EE" strokeWidth="0.4" opacity="0.4" />
              <line x1="290" y1="180" x2="310" y2="280" stroke="#F5F2EE" strokeWidth="0.4" opacity="0.4" />
              <line x1="380" y1="170" x2="390" y2="280" stroke="#F5F2EE" strokeWidth="0.4" opacity="0.4" />
            </g>

            {/* MOROCCO + N AFRICA */}
            <g opacity="0.55">
              <path
                d="M 770 360 L 830 340 L 890 350 L 920 380 L 905 410 L 870 425 L 820 420 L 780 405 Z"
                fill="#F5F2EE"
                opacity="0.06"
                stroke="#F5F2EE"
                strokeWidth="0.6"
              />
            </g>

            {/* ARC route */}
            <path
              ref={pathRef}
              d="M 800 365 Q 540 80 290 220"
              fill="none"
              stroke="url(#map-arc-grad)"
              strokeWidth="2.2"
              strokeDasharray={pathLength}
              strokeDashoffset={dashOffset}
              strokeLinecap="round"
            />
            {/* faint base track */}
            <path
              d="M 800 365 Q 540 80 290 220"
              fill="none"
              stroke="#F5F2EE"
              strokeWidth="0.7"
              opacity="0.12"
            />

            {/* Origin marker — Casablanca */}
            <circle cx="800" cy="365" r="38" fill="url(#origin-glow)" />
            <circle cx="800" cy="365" r="6" fill="#E8A045" />
            <circle cx="800" cy="365" r="14" fill="none" stroke="#E8A045" strokeWidth="0.8" opacity="0.5" />
            <line x1="800" y1="355" x2="800" y2="320" stroke="#E8A045" strokeWidth="0.8" />
            <text
              x="800"
              y="312"
              textAnchor="middle"
              fontFamily="'Fraunces', serif"
              fontSize="22"
              fontStyle="italic"
              fill="#F5F2EE"
            >
              {t.map.origin}
            </text>
            <text
              x="800"
              y="294"
              textAnchor="middle"
              fontFamily="'DM Mono', monospace"
              fontSize="9"
              letterSpacing="3"
              fill="#E8A045"
            >
              {t.map.originLabel.toUpperCase()}
            </text>

            {/* Destination — Montreal */}
            <circle cx="290" cy="220" r="38" fill="url(#dest-glow)" />
            <circle cx="290" cy="220" r="6" fill="#2FBFAD" />
            <circle cx="290" cy="220" r="14" fill="none" stroke="#2FBFAD" strokeWidth="0.8" opacity="0.5" />
            <line x1="290" y1="210" x2="290" y2="170" stroke="#2FBFAD" strokeWidth="0.8" />
            <text
              x="290"
              y="162"
              textAnchor="middle"
              fontFamily="'Fraunces', serif"
              fontSize="22"
              fontStyle="italic"
              fill="#F5F2EE"
            >
              {t.map.destination}
            </text>
            <text
              x="290"
              y="144"
              textAnchor="middle"
              fontFamily="'DM Mono', monospace"
              fontSize="9"
              letterSpacing="3"
              fill="#2FBFAD"
            >
              {t.map.destinationLabel.toUpperCase()}
            </text>

            {/* Stat callout */}
            <text
              x="1100"
              y="490"
              textAnchor="end"
              fontFamily="'Fraunces', serif"
              fontSize="48"
              fontStyle="italic"
              fill="#F5F2EE"
            >
              12 000+
            </text>
            <text
              x="1100"
              y="510"
              textAnchor="end"
              fontFamily="'DM Mono', monospace"
              fontSize="10"
              letterSpacing="3"
              fill="#E8A045"
            >
              DOSSIERS TRAITÉS
            </text>
            <text
              x="100"
              y="510"
              textAnchor="start"
              fontFamily="'DM Mono', monospace"
              fontSize="10"
              letterSpacing="3"
              fill="#F5F2EE"
              opacity="0.55"
            >
              {t.map.distance.toUpperCase()}
            </text>
          </svg>
        </motion.div>
      </div>
    </section>
  );
};

export default MapSection;
